import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { useCreateEvent, useEvents, useVenues } from '@/hooks/useDesk';
import { formatDate } from '@/lib/format';
import { formatEventPlace } from '@/types/desk';

export function EventsPage() {
  const { data, isLoading, isError, error } = useEvents();
  const venues = useVenues();
  const create = useCreateEvent();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [venueId, setVenueId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [venueLabel, setVenueLabel] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [guestCount, setGuestCount] = useState('');

  const roomsForVenue = useMemo(() => {
    const venue = (venues.data || []).find((v) => v.id === venueId);
    return (venue?.domeec_rooms || []).filter((r) => r.active !== false);
  }, [venues.data, venueId]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const selectedVenue = (venues.data || []).find((v) => v.id === venueId);
    const selectedRoom = roomsForVenue.find((r) => r.id === roomId);
    await create.mutateAsync({
      name,
      venue_id: venueId || null,
      room_id: roomId || null,
      venue_label:
        venueLabel.trim() ||
        [selectedVenue?.name, selectedRoom?.name].filter(Boolean).join(' · ') ||
        null,
      starts_at: startsAt ? new Date(startsAt).toISOString() : null,
      guest_count: guestCount ? Number(guestCount) : null,
      status: 'draft',
    });
    setName('');
    setVenueId('');
    setRoomId('');
    setVenueLabel('');
    setStartsAt('');
    setGuestCount('');
    setOpen(false);
  }

  return (
    <div>
      <PageHeader
        title="Events"
        description="Confirmed and in-progress venue events."
        actions={
          <button
            type="button"
            className="rounded-lg bg-dome-forest px-3 py-2 text-sm font-medium text-dome-sand"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Close' : 'New event'}
          </button>
        }
      />

      {open ? (
        <form
          onSubmit={onSubmit}
          className="mb-5 grid gap-3 rounded-xl border border-[var(--border)] bg-white/80 p-4 sm:grid-cols-2"
        >
          <label className="text-sm sm:col-span-2">
            <span className="mb-1 block font-medium">Name</span>
            <input
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Venue</span>
            <select
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              value={venueId}
              onChange={(e) => {
                setVenueId(e.target.value);
                setRoomId('');
              }}
            >
              <option value="">— Free-text / none —</option>
              {(venues.data || []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                  {v.city ? ` (${v.city})` : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Room</span>
            <select
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              value={roomId}
              disabled={!venueId}
              onChange={(e) => setRoomId(e.target.value)}
            >
              <option value="">— Optional —</option>
              {roomsForVenue.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                  {r.capacity != null ? ` · ${r.capacity} pax` : ''}
                </option>
              ))}
            </select>
          </label>
          {!venueId ? (
            <label className="text-sm sm:col-span-2">
              <span className="mb-1 block font-medium">Venue label</span>
              <input
                className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
                value={venueLabel}
                onChange={(e) => setVenueLabel(e.target.value)}
                placeholder="Free-text until venues are imported"
              />
            </label>
          ) : null}
          <label className="text-sm">
            <span className="mb-1 block font-medium">Starts</span>
            <input
              type="datetime-local"
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Guests</span>
            <input
              type="number"
              min={1}
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              value={guestCount}
              onChange={(e) => setGuestCount(e.target.value)}
            />
          </label>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={create.isPending}
              className="rounded-lg bg-dome-brass px-4 py-2 text-sm font-medium text-dome-ink"
            >
              {create.isPending ? 'Saving…' : 'Save event'}
            </button>
          </div>
        </form>
      ) : null}

      {isLoading ? (
        <p className="animate-pulse-soft text-sm text-[var(--muted)]">Loading…</p>
      ) : null}
      {isError ? (
        <p className="text-sm text-dome-danger">{(error as Error).message}</p>
      ) : null}
      {!isLoading && data && data.length === 0 ? (
        <EmptyState
          title="No events yet"
          body="Convert an enquiry or create an event to start the desk workflow."
        />
      ) : null}
      {data && data.length > 0 ? (
        <DataTable headers={['Event', 'Venue / room', 'Starts', 'Guests', 'Status', 'Client']}>
          {data.map((row) => (
            <tr key={row.id}>
              <td className="px-3 py-2 font-medium">
                <Link
                  to={`/events/${row.id}`}
                  className="text-dome-forest underline-offset-2 hover:underline"
                >
                  {row.name}
                </Link>
              </td>
              <td className="px-3 py-2">{formatEventPlace(row)}</td>
              <td className="px-3 py-2">{formatDate(row.starts_at)}</td>
              <td className="px-3 py-2">{row.guest_count ?? '—'}</td>
              <td className="px-3 py-2">
                <StatusBadge status={row.status} />
              </td>
              <td className="px-3 py-2">
                {row.domeec_clients?.company_name || '—'}
              </td>
            </tr>
          ))}
        </DataTable>
      ) : null}
    </div>
  );
}
